// 报修记录列表 — kefu /hd-online/repair/list
// 路由：/m/repair-list?storeId=
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, App as AntdApp, Button, Card, Descriptions, Drawer, Empty, Image, Space, Spin, Tag, Typography } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { fetchRepairDetailResult, fetchRepairListResult, fetchRepairListWithDateFallback, remindRepair, type RepairItem } from '../api/repair.js';
import { fetchStores, type StoreLite } from '../components/StoreSelector.js';
import { useRegion } from '../hooks/useRegion.js';
import { resolveExternalImageUrl } from '../utils/media-url.js';

export default function RepairListPage() {
	const region = useRegion();
	return region === 'apac' ? <NativeRepairRecordParity /> : <RepairListClassic />;
}

function RepairListClassic() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [params] = useSearchParams();
	const storeId = params.get('storeId') ?? undefined;
	const [items, setItems] = useState<RepairItem[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [active, setActive] = useState<RepairItem | null>(null);

	useEffect(() => {
		setItems(null);
		setError(null);
		void fetchRepairListResult({ region: 'cn', store_id: storeId, page: 1, count: 30 }).then((result) => {
			setItems(result.items);
			setError(result.ok ? null : result.msg ?? '当前地区暂不可用');
		});
	}, [storeId]);

	return (
		<div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
			<div style={{ display: 'flex', alignItems: 'center', padding: '12px 12px', background: '#fff', borderBottom: '1px solid #f0f0f0' }}>
				<Button type="text" shape="circle" icon={<LeftOutlined />} onClick={() => navigate(-1)} />
				<Typography.Text strong style={{ flex: 1, textAlign: 'center', fontSize: 17 }}>
					{t('repair.history')}
				</Typography.Text>
				<div style={{ width: 32 }} />
			</div>

			<div style={{
				padding: 12,
				display: 'grid',
				gridTemplateColumns: 'repeat(auto-fill, minmax(min(380px, 100%), 1fr))',
				gap: 10,
			}}>
				{error && <Alert type="warning" message={error} showIcon style={{ gridColumn: '1 / -1' }} />}
				{!items && <Spin />}
				{items && items.length === 0 && <Empty description={t('common.no_data')} style={{ marginTop: 80, gridColumn: '1 / -1' }} />}
				{items && items.map((it, i) => (
					<Card
						key={String(it.id ?? i)}
						style={{ borderRadius: 10 }}
						styles={{ body: { padding: 14 } }}
					>
						<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
							<Typography.Text strong>{it.store_name ?? it.store_id ?? `#${it.id}`}</Typography.Text>
							{it.status != null && <Tag color={repairStatusColor(it.status)}>{repairStatusLabel(it.status)}</Tag>}
						</div>
						{it.describe && <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ margin: '0 0 6px', fontSize: 13, color: '#666' }}>{it.describe}</Typography.Paragraph>}
						<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#999' }}>
							<span>{it.contact_user} {it.contact_phone}</span>
							<span>{it.create_time}</span>
						</div>
						<Button block style={{ marginTop: 12 }} onClick={() => setActive(it)}>
							{t('common.detail', { defaultValue: '查看详情' })}
						</Button>
					</Card>
				))}
			</div>
			<RepairDetailDrawer item={active} onClose={() => setActive(null)} />
		</div>
	);
}

function NativeRepairRecordParity() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [params] = useSearchParams();
	const routeStoreId = params.get('storeId') ?? '';
	const [stores, setStores] = useState<StoreLite[]>([]);
	const [storeFilter, setStoreFilter] = useState(routeStoreId);
	const [statusFilter, setStatusFilter] = useState('');
	const [range, setRange] = useState<[Dayjs, Dayjs]>(() => [dayjs().subtract(6, 'day'), dayjs()]);
	const [items, setItems] = useState<RepairItem[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [filterDrawer, setFilterDrawer] = useState<'store' | 'status' | 'date' | null>(null);
	const [active, setActive] = useState<RepairItem | null>(null);

	useEffect(() => {
		void fetchStores().then(setStores).catch(() => setStores([]));
	}, []);

	const load = () => {
		setItems(null);
		setError(null);
		void fetchRepairListWithDateFallback({
			region: 'apac',
			store_id: storeFilter || undefined,
			page: 1,
			count: 50,
			status: statusFilter || '1,2,3',
			start_date: range[0].format('YYYY-MM-DD'),
			end_date: range[1].format('YYYY-MM-DD'),
		}).then((result) => {
			if (result.ok) {
				setItems(result.items);
				setError(null);
			} else {
				setItems([]);
				setError(result.msg ?? t('native.common.operation_failed'));
			}
		}).catch((err: unknown) => {
			setItems([]);
			setError(err instanceof Error ? err.message : String(err));
		});
	};

	useEffect(() => {
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [storeFilter, statusFilter, range]);

	const visibleItems = (items ?? []).filter((item) => repairMatchesStatus(item, statusFilter));
	const storeLabel = storeFilter ? stores.find((store) => store.store_id === storeFilter)?.store ?? t('native.common.store') : t('native.common.all_stores');
	const statusLabel = statusFilter === '1'
		? t('native.repair.status_submitted')
		: statusFilter === '2'
			? t('native.repair.status_processing')
			: t('native.common.all_status');
	const dateLabel = `${range[0].format('YYYY-MM-DD')}\n${range[1].format('YYYY-MM-DD')}`;

	return (
		<div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
			<NativeHeader title={t('native.repair.record')} onBack={() => navigate(-1)} />
			<div style={{ background: '#fff', display: 'grid', gridTemplateColumns: '1.15fr 1fr 1fr', borderBottom: '1px solid #eeeeee' }}>
				<NativeFilterButton label={dateLabel} onClick={() => setFilterDrawer('date')} />
				<NativeFilterButton label={storeLabel} onClick={() => setFilterDrawer('store')} />
				<NativeFilterButton label={statusLabel} onClick={() => setFilterDrawer('status')} />
			</div>
			{items == null ? (
				<div style={{ paddingTop: 80, textAlign: 'center' }}><Spin /></div>
			) : error ? (
				<NativeErrorState message={error} onRefresh={load} />
			) : visibleItems.length === 0 ? (
				<NativeNoData onRefresh={load} />
			) : (
				<div style={{ padding: 12 }}>
					{visibleItems.map((item, index) => (
						<button
							key={String(item.id ?? item.no ?? index)}
							type="button"
							onClick={() => setActive(item)}
							style={{
								width: '100%',
								border: 0,
								background: '#fff',
								padding: 14,
								marginBottom: 10,
								textAlign: 'start',
								cursor: 'pointer',
							}}
						>
							<div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
								<span style={{ color: '#20242b', fontSize: 16, fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
									{`${t('native.repair.store_prefix')}: ${String(item.store_name ?? item.store_id ?? `#${index + 1}`)}`}
								</span>
								<span style={{ color: '#999', fontSize: 13, flex: '0 0 auto' }}>
									<span aria-hidden="true" style={repairStatusDotStyle(item.status)} />
									<span>{repairStatusLabel(item.status, true)}</span>
								</span>
							</div>
							{item.describe && <div style={{ marginTop: 8, color: '#666', fontSize: 14, lineHeight: 1.4 }}>{`${t('native.repair.fault_description')}: ${String(item.describe)}`}</div>}
							<div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>{repairTimeText(item) ? `${t('native.repair.submission_time')}: ${repairTimeText(item)}` : ''}</div>
						</button>
					))}
				</div>
			)}
			<NativeBottomSheet open={filterDrawer === 'store'} title={t('native.common.all_stores')} onClose={() => setFilterDrawer(null)}>
				<NativeSheetOption active={!storeFilter} onClick={() => { setStoreFilter(''); setFilterDrawer(null); }}>
					{t('native.common.all_stores')}
				</NativeSheetOption>
				{stores.map((store) => (
					<NativeSheetOption key={store.store_id} active={storeFilter === store.store_id} onClick={() => { setStoreFilter(store.store_id); setFilterDrawer(null); }}>
						{store.store}
					</NativeSheetOption>
				))}
			</NativeBottomSheet>
			<NativeBottomSheet open={filterDrawer === 'status'} title={t('native.common.all_status')} onClose={() => setFilterDrawer(null)}>
				{[
					{ value: '', label: t('native.common.all_status') },
					{ value: '1', label: t('native.repair.status_submitted') },
					{ value: '2', label: t('native.repair.status_processing') },
				].map((item) => (
					<NativeSheetOption key={item.value || 'all'} active={statusFilter === item.value} onClick={() => { setStatusFilter(item.value); setFilterDrawer(null); }}>
						{item.label}
					</NativeSheetOption>
				))}
			</NativeBottomSheet>
			<NativeBottomSheet open={filterDrawer === 'date'} title={t('native.repair.date_range')} onClose={() => setFilterDrawer(null)}>
				<div style={{ display: 'grid', gap: 12 }}>
					<NativeDateInput
						label={`${t('native.common.date')} 1`}
						value={range[0].format('YYYY-MM-DD')}
						onChange={(value) => {
							const next = dayjs(value);
							if (next.isValid()) setRange([next, range[1]]);
						}}
					/>
					<NativeDateInput
						label={`${t('native.common.date')} 2`}
						value={range[1].format('YYYY-MM-DD')}
						onChange={(value) => {
							const next = dayjs(value);
							if (next.isValid()) setRange([range[0], next]);
						}}
					/>
					<button
						type="button"
						onClick={() => setFilterDrawer(null)}
						style={{
							width: '100%',
							height: 44,
							border: 0,
							borderRadius: 4,
							background: '#ff6b3a',
							color: '#fff',
							fontSize: 16,
						}}
					>
						{t('native.common.confirm')}
					</button>
				</div>
			</NativeBottomSheet>
			<NativeRepairDetailDrawer item={active} onClose={() => setActive(null)} />
		</div>
	);
}

function NativeBottomSheet({
	open,
	title,
	children,
	onClose,
}: {
	open: boolean;
	title: string;
	children: ReactNode;
	onClose: () => void;
}) {
	if (!open) return null;
	return (
		<div
			role="presentation"
			onClick={onClose}
			style={{
				position: 'fixed',
				top: 0,
				right: 0,
				bottom: 0,
				left: 0,
				zIndex: 1000,
				display: 'flex',
				alignItems: 'flex-end',
				background: 'rgba(0, 0, 0, 0.28)',
			}}
		>
			<div
				role="dialog"
				aria-modal="true"
				aria-label={title}
				onClick={(event) => event.stopPropagation()}
				style={{
					width: '100%',
					maxHeight: '72vh',
					background: '#fff',
					borderTopLeftRadius: 8,
					borderTopRightRadius: 8,
					overflow: 'hidden',
					boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.16)',
				}}
			>
				<div style={{ height: 52, display: 'flex', alignItems: 'center', borderBottom: '1px solid #eeeeee' }}>
					<button
						type="button"
						onClick={onClose}
						style={{ width: 52, height: 52, border: 0, background: 'transparent', color: '#8b929c', fontSize: 28, lineHeight: 1 }}
					>
						x
					</button>
					<div style={{ flex: 1, color: '#20242b', fontSize: 17, fontWeight: 600, textAlign: 'center', paddingRight: 52 }}>
						{title}
					</div>
				</div>
				<div style={{ padding: 16, overflowY: 'auto', maxHeight: 'calc(72vh - 52px)' }}>{children}</div>
			</div>
		</div>
	);
}

function NativeSheetOption({
	active,
	children,
	onClick,
}: {
	active: boolean;
	children: ReactNode;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			style={{
				width: '100%',
				minHeight: 44,
				marginBottom: 10,
				border: active ? 0 : '1px solid #dddddd',
				borderRadius: 4,
				background: active ? '#ff6b3a' : '#fff',
				color: active ? '#fff' : '#20242b',
				fontSize: 16,
			}}
		>
			{children}
		</button>
	);
}

function NativeDateInput({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
	return (
		<label style={{ display: 'grid', gap: 6, color: '#5b6470', fontSize: 14 }}>
			<span>{label}</span>
			<input
				type="date"
				value={value}
				onChange={(event) => onChange(event.currentTarget.value)}
				style={{
					height: 42,
					border: '1px solid #dcdfe6',
					borderRadius: 4,
					padding: '0 10px',
					color: '#20242b',
					fontSize: 15,
					background: '#fff',
				}}
			/>
		</label>
	);
}

function NativeRepairRecord() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [params] = useSearchParams();
	const storeId = params.get('storeId') ?? undefined;
	const [items, setItems] = useState<RepairItem[] | null>(null);

	const load = () => {
		setItems(null);
		void fetchRepairListResult({ region: 'apac', store_id: storeId, page: 1, count: 30 }).then((result) => {
			setItems(result.ok ? result.items : []);
		}).catch(() => setItems([]));
	};

	useEffect(() => {
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [storeId]);

	return (
		<div style={{ background: '#f5f5f5', minHeight: '100vh' }}>
			<NativeHeader title={t('native.repair.record')} onBack={() => navigate(-1)} />
			<div style={{ background: '#fff', display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr', borderBottom: '1px solid #eeeeee' }}>
				<NativeFilterButton label={t('native.common.all_stores')} />
				<NativeFilterButton label={t('native.common.all_status')} />
				<NativeFilterButton label={`${formatNativeDate(daysBefore(6))}\n${formatNativeDate(new Date())}`} />
			</div>
			{items == null ? (
				<div style={{ paddingTop: 80, textAlign: 'center' }}><Spin /></div>
			) : items.length === 0 ? (
				<NativeNoData onRefresh={load} />
			) : (
				<div style={{ padding: 12 }}>
					{items.map((item, index) => (
						<button
							key={String(item.id ?? index)}
							type="button"
							style={{
								width: '100%',
								border: 0,
								background: '#fff',
								padding: 14,
								marginBottom: 10,
								textAlign: 'start',
							}}
						>
							<div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
								<span style={{ color: '#20242b', fontSize: 16, fontWeight: 500 }}>{`${t('native.repair.store_prefix')}: ${String(item.store_name ?? item.store_id ?? `#${item.id ?? index + 1}`)}`}</span>
								<span style={{ color: '#ff8a34', fontSize: 13 }}>{item.status != null ? repairStatusLabel(item.status) : ''}</span>
							</div>
							{item.describe && <div style={{ marginTop: 8, color: '#666', fontSize: 14, lineHeight: 1.4 }}>{`${t('native.repair.fault_description')}: ${String(item.describe)}`}</div>}
							<div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>{item.create_time ? `${t('native.repair.submission_time')}: ${String(item.create_time)}` : ''}</div>
						</button>
					))}
				</div>
			)}
		</div>
	);
}

function NativeHeader({ title, onBack }: { title: string; onBack: () => void }) {
	const { t } = useTranslation();
	return (
		<header
			style={{
				height: 48,
				background: '#fff',
				borderBottom: '1px solid #eeeeee',
				display: 'grid',
				gridTemplateColumns: '56px minmax(0, 1fr) 56px',
				alignItems: 'center',
				position: 'sticky',
				top: 0,
				zIndex: 10,
			}}
		>
			<button
				type="button"
				aria-label={t('common.back')}
				onClick={onBack}
				style={{ height: 48, border: 0, background: 'transparent', color: '#30343b', fontSize: 18 }}
			>
				<LeftOutlined />
			</button>
			<div style={{ textAlign: 'center', color: '#20242b', fontSize: 16, fontWeight: 500 }}>
				{title}
			</div>
			<span />
		</header>
	);
}

function NativeFilterButton({ label, onClick }: { label: string; onClick?: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			style={{
				height: 48,
				border: 0,
				borderRight: '1px solid #eeeeee',
				background: '#fff',
				color: '#555',
				fontSize: 14,
				whiteSpace: 'pre-line',
			}}
		>
			{label}
		</button>
	);
}

function NativeNoData({ onRefresh }: { onRefresh: () => void }) {
	const { t } = useTranslation();
	return (
		<div style={{ minHeight: 'calc(100vh - 96px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
			<div style={{ transform: 'translateY(-64px)', textAlign: 'center' }}>
				<div style={{ color: '#9a9a9a', fontSize: 14, lineHeight: 1.2 }}>{t('native.common.no_data')}</div>
				<button
					type="button"
					onClick={onRefresh}
					style={{ marginTop: 12, border: 0, background: 'transparent', color: '#ff8a34', fontSize: 17 }}
				>
					{t('native.common.refresh')}
				</button>
			</div>
		</div>
	);
}

function NativeErrorState({ message, onRefresh }: { message: string; onRefresh: () => void }) {
	const { t } = useTranslation();
	return (
		<div style={{ minHeight: 'calc(100vh - 96px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
			<div style={{ transform: 'translateY(-64px)', textAlign: 'center', maxWidth: 360 }}>
				<div style={{ color: '#9a3a2d', fontSize: 14, lineHeight: 1.45, wordBreak: 'break-word' }}>{message}</div>
				<button
					type="button"
					onClick={onRefresh}
					style={{ marginTop: 12, border: 0, background: 'transparent', color: '#ff8a34', fontSize: 15 }}
				>
					{t('native.common.refresh')}
				</button>
			</div>
		</div>
	);
}

function daysBefore(days: number) {
	const date = new Date();
	date.setDate(date.getDate() - days);
	return date;
}

function formatNativeDate(date: Date) {
	const yyyy = date.getFullYear();
	const mm = String(date.getMonth() + 1).padStart(2, '0');
	const dd = String(date.getDate()).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
}

function RepairDetailDrawer({ item, onClose }: { item: RepairItem | null; onClose: () => void }) {
	const { t } = useTranslation();
	const { message } = AntdApp.useApp();
	const [detail, setDetail] = useState<RepairItem | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [reminding, setReminding] = useState(false);
	const viewItem = detail ?? item;
	const images = repairImages(viewItem);
	const repairId = item?.id ?? detail?.id;

	useEffect(() => {
		if (!item?.id) {
			setDetail(null);
			setError(null);
			return;
		}
		setDetail(null);
		setError(null);
		setLoading(true);
		void fetchRepairDetailResult(item.id)
			.then((result) => {
				if (result.ok) {
					setDetail(result.item ?? null);
				} else {
					setError(result.msg ?? '报修详情接口暂不可用');
				}
			})
			.finally(() => setLoading(false));
	}, [item]);

	const onRemind = async () => {
		if (!repairId) return;
		setReminding(true);
		const result = await remindRepair(repairId);
		setReminding(false);
		if (result.ok) {
			message.success(result.msg || '已提交催单');
		} else {
			message.error(result.msg || '催单失败');
		}
	};

	return (
		<Drawer
			open={Boolean(item)}
			onClose={onClose}
			title={t('repair.detail', { defaultValue: '报修详情' })}
			width={520}
		>
			{viewItem && (
				<Space direction="vertical" size="middle" style={{ width: '100%' }}>
					{loading && <Spin />}
					{error && (
						<Alert
							type="warning"
							showIcon
							message={error}
							description={t('repair.detail_fallback', {
								defaultValue: '当前退回展示报修列表接口返回的字段。',
							})}
						/>
					)}
					<Descriptions column={1} size="small" bordered>
						<Descriptions.Item label={t('repair.id', { defaultValue: '报修单号' })}>
							{String(viewItem.id ?? '-')}
						</Descriptions.Item>
						<Descriptions.Item label={t('repair.store')}>
							{String(viewItem.store_name ?? viewItem.store_id ?? '-')}
						</Descriptions.Item>
						<Descriptions.Item label={t('repair.status', { defaultValue: '状态' })}>
							{viewItem.status != null ? <Tag color={repairStatusColor(viewItem.status)}>{repairStatusLabel(viewItem.status)}</Tag> : '-'}
						</Descriptions.Item>
						<Descriptions.Item label={t('repair.contact')}>
							{String(viewItem.contact_user ?? '-')}
						</Descriptions.Item>
						<Descriptions.Item label={t('repair.contact_phone')}>
							{String(viewItem.contact_phone ?? '-')}
						</Descriptions.Item>
						<Descriptions.Item label={t('repair.describe')}>
							{String(viewItem.describe ?? '-')}
						</Descriptions.Item>
						<Descriptions.Item label={t('repair.create_time', { defaultValue: '提交时间' })}>
							{String(viewItem.create_time ?? '-')}
						</Descriptions.Item>
						<Descriptions.Item label={t('repair.update_time', { defaultValue: '更新时间' })}>
							{String(viewItem.update_time ?? viewItem.modify_time ?? '-')}
						</Descriptions.Item>
					</Descriptions>
					<div>
						<Typography.Text strong>{t('repair.images', { defaultValue: '报修图片' })}</Typography.Text>
						{images.length > 0 ? (
							<Image.PreviewGroup>
								<Space wrap style={{ marginTop: 8 }}>
									{images.map((src) => (
										<Image key={src} src={src} fallback={resolveExternalImageUrl(src).fallback || undefined} width={96} height={96} style={{ objectFit: 'cover', borderRadius: 6 }} />
									))}
								</Space>
							</Image.PreviewGroup>
						) : (
							<Empty
								image={Empty.PRESENTED_IMAGE_SIMPLE}
								description={t('repair.no_images', { defaultValue: '列表接口未返回图片' })}
							/>
						)}
					</div>
					<Alert
						type="warning"
						showIcon
						message={t('repair.status_flow_pending', {
							defaultValue: 'APK 静态证据只确认了详情和催单接口；处理、关闭、验收等状态流转需要继续抓包确认。',
						})}
					/>
					<Button block disabled={!repairId} loading={reminding} onClick={() => void onRemind()}>
						{t('repair.remind', { defaultValue: '催处理' })}
					</Button>
				</Space>
			)}
		</Drawer>
	);
}

function NativeRepairDetailDrawer({ item, onClose }: { item: RepairItem | null; onClose: () => void }) {
	const { t } = useTranslation();
	const [detail, setDetail] = useState<RepairItem | null>(null);
	const [loading, setLoading] = useState(false);
	const detailId = item?.id ?? item?.repair_id ?? item?.no;
	const viewItem = detail ?? item;
	const images = repairImages(viewItem);

	useEffect(() => {
		if (!detailId) {
			setDetail(null);
			return;
		}
		setDetail(null);
		setLoading(true);
		void fetchRepairDetailResult(detailId as string | number)
			.then((result) => setDetail(result.ok ? result.item ?? null : null))
			.finally(() => setLoading(false));
	}, [detailId]);

	return (
		<Drawer open={Boolean(item)} onClose={onClose} title={t('native.common.details')} placement="right" width="92vw">
			{viewItem && (
				<div style={{ color: '#20242b', fontSize: 15 }}>
					{loading && <Spin />}
					<NativeDetailLine label={t('native.repair.repair_no')} value={String(viewItem.no ?? viewItem.id ?? '-')} />
					<NativeDetailLine label={t('native.repair.store_prefix')} value={String(viewItem.store_name ?? viewItem.store_id ?? '-')} />
					<NativeDetailLine label={t('native.common.all_status')} value={repairStatusLabel(viewItem.status, true)} />
					<NativeDetailLine label={t('native.repair.contact_person')} value={String(viewItem.contact_user ?? '-')} />
					<NativeDetailLine label={t('native.repair.contact_number')} value={String(viewItem.contact_phone ?? '-')} />
					<NativeDetailLine label={t('native.repair.submission_time')} value={repairTimeText(viewItem) || '-'} />
					<div style={{ marginTop: 16, padding: 12, background: '#f7f7f7' }}>
						<div style={{ marginBottom: 8, color: '#666' }}>{t('native.repair.fault_description')}</div>
						<div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{String(viewItem.describe ?? '-')}</div>
					</div>
					<div style={{ marginTop: 18 }}>
						<div style={{ marginBottom: 8, color: '#666' }}>{t('native.repair.images')}</div>
						{images.length > 0 ? (
							<Image.PreviewGroup>
								<Space wrap>
									{images.map((src) => (
										<Image key={src} src={src} fallback={resolveExternalImageUrl(src).fallback || undefined} width={86} height={86} style={{ objectFit: 'cover' }} />
									))}
								</Space>
							</Image.PreviewGroup>
						) : (
							<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('native.repair.no_images')} />
						)}
					</div>
				</div>
			)}
		</Drawer>
	);
}

function NativeDetailLine({ label, value }: { label: string; value: string }) {
	return (
		<div style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: '1px solid #eeeeee' }}>
			<span style={{ width: 118, color: '#777', flex: '0 0 auto' }}>{label}</span>
			<span style={{ flex: 1, minWidth: 0, textAlign: 'right', overflowWrap: 'anywhere' }}>{value}</span>
		</div>
	);
}

function repairStatusLabel(status: RepairItem['status'], native = false) {
	const value = String(status ?? '');
	const lower = value.toLowerCase();
	if (native) {
		if (repairStatusCode(status) === '1' || lower.includes('submit')) return 'Submitted';
		if (repairStatusCode(status) === '2' || lower.includes('process')) return 'Processing';
		return value || '-';
	}
	const map: Record<string, string> = {
		'0': '待处理',
		'1': '处理中',
		'2': '已完成',
		'3': '已关闭',
		'4': '已取消',
	};
	return map[value] ?? value;
}

function repairStatusColor(status: RepairItem['status']) {
	const value = String(status ?? '');
	if (value === '0') return 'orange';
	if (value === '1') return 'blue';
	if (value === '2') return 'green';
	if (value === '3' || value === '4') return 'default';
	return undefined;
}

function repairStatusCode(status: RepairItem['status']): string {
	const value = String(status ?? '').trim();
	if (/^\d+$/.test(value)) return value;
	const lower = value.toLowerCase();
	if (lower.includes('submit')) return '1';
	if (lower.includes('process')) return '2';
	return value;
}

function repairStatusDotStyle(status: RepairItem['status']): CSSProperties {
	const color = repairStatusColor(status);
	return {
		display: 'inline-block',
		width: 7,
		height: 7,
		marginRight: 5,
		borderRadius: '50%',
		background: color === 'green' ? '#52c41a' : color === 'blue' ? '#1677ff' : color === 'orange' ? '#faad14' : '#c9cdd4',
		verticalAlign: 'middle',
	};
}

function repairMatchesStatus(item: RepairItem, statusFilter: string): boolean {
	if (!statusFilter) return true;
	return repairStatusCode(item.status) === statusFilter;
}

function repairMatchesRange(item: RepairItem, range: [Dayjs, Dayjs]): boolean {
	const time = repairTimeText(item);
	if (!time) return true;
	const date = dayjs(time);
	if (!date.isValid()) return true;
	return (date.isAfter(range[0].startOf('day')) || date.isSame(range[0], 'day'))
		&& (date.isBefore(range[1].endOf('day')) || date.isSame(range[1], 'day'));
}

function repairTimeText(item: RepairItem | null): string {
	if (!item) return '';
	const raw = item.create_time ?? item.created_at ?? item.submit_time ?? item.ctime ?? item.time;
	return raw == null ? '' : String(raw);
}

function repairImages(item: RepairItem | null): string[] {
	if (!item) return [];
	const raw = item.images ?? item.image ?? item.pics ?? item.pictures ?? item.imgs;
	return normalizeImageValue(raw);
}

function normalizeImageValue(value: unknown): string[] {
	if (!value) return [];
	if (Array.isArray(value)) {
		return value.reduce<string[]>((acc, entry) => acc.concat(normalizeImageValue(entry)), []);
	}
	if (typeof value === 'object') {
		const record = value as Record<string, unknown>;
		const url = record.url ?? record.src ?? record.path ?? record.image_url ?? record.pic_url ?? record.pic_path ?? record.img_url ?? record.thumbnail ?? record.thumb;
		return typeof url === 'string' ? normalizeImageValue(url) : [];
	}
	if (typeof value !== 'string') return [];
	const text = value.trim();
	if (!text) return [];
	if (text.startsWith('[') || text.startsWith('{')) {
		try {
			return normalizeImageValue(JSON.parse(text));
		} catch {
			return [];
		}
	}
	return text
		.split(',')
		.map((part) => resolveExternalImageUrl(part).primary)
		.filter(Boolean);
}
